<template>
  <div id="diaporama">
    <div id="navigation" v-if="listeDiapos.length > 1">
      <button @click="goToPreviousSlide" aria-label="previous"></button>
      <div v-for="(media, index) in listeDiapos" :key="index" @click="changeSlide(index)">
        <div class="dot" :class="{ active: index === currentIndex }"></div>
      </div>
      <button @click="goToNextSlide" aria-label="next" style="transform: rotate(180deg);"></button>
    </div>

    <div
      id="slides"
      :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchCancel"
    >
      <div v-for="(media, index) in listeDiapos" :key="`container-${index}`">
        <div
          v-if="isImage(media)"
          class="slide"
          :style="{
            backgroundImage: imagesLoaded[index] ? `url(diapo/${resolution === 'mobile' ? '500px' : '1000px'}/${media})` : 'none',
          }"
        >
          <img
            :src="`diapo/${resolution === 'mobile' ? '500px' : '1000px'}/${media}`"
            @load="onImageLoaded(index)"
            @error="onImageLoaded(index)"
            :class="{'img-loaded': imagesLoaded[index]}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0; pointer-events: none;"
          />
          <SimpleLoader :loading="!imagesLoaded[index]" />
        </div>
        <div v-else-if="isVideo(media)" class="slide">
          <lecteurVideo 
            v-show="isComponentReady && Math.abs(index - currentIndex) <= 1"
            :ref="`video-${index}`"
            :key="`video-${index}-${media}`"
            :videoSrc="`diapo/videos/${media}`" 
            :resolution="resolution"
          />
          <div v-show="!isComponentReady || Math.abs(index - currentIndex) > 1" class="video-placeholder">
            <SimpleLoader :loading="true" />
            <div class="video-loading">{{ isComponentReady ? 'Vidéo' : 'Chargement...' }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import lecteurVideo from './lecteurVideo.vue';
import SimpleLoader from '../SimpleLoader.vue';

export default {
  name: 'lecteurDiapo',
  emits: ['images-loaded'],
  components: { lecteurVideo, SimpleLoader },
  props: {
    listeDiapos: Array,
    resolution: String,
    racineName: String,
    sectionActive: Number
  },
  data() {
    return {
      currentIndex: 0,
      isComponentReady: false,
      activeVideoIndex: null,
      touchStartX: null,
      touchCurrentX: null,
      // ✅ NOUVEAU : Variables pour améliorer la gestion tactile
      isSwipeInProgress: false,
      swipeThreshold: 50, // Seuil de détection du swipe
      touchStartTime: null,
      maxSwipeTime: 500, // Temps max pour un swipe (ms)
      imagesLoaded: {}, // { index: true/false }
    };
  },
  computed: {
    currentMedia() {
      return this.listeDiapos[this.currentIndex] || '';
    },

    // ✅ NOUVEAU : Computed pour savoir si la slide actuelle est une vidéo
    isCurrentSlideVideo() {
      return this.isVideo(this.currentMedia);
    }
  },
  methods: {
    ...mapActions(['updateStoreUrl']),
    
    // ✅ NOUVEAU : Méthode pour changer de slide via les dots
    changeSlide(index) {
      if (index !== this.currentIndex) {
        this.stopAllVideos(); // ✅ Arrêter les vidéos avant de changer
        this.currentIndex = index;
        this.updateActiveVideo(); // ✅ NOUVEAU
      }
    },

    // ✅ NOUVEAU : Méthode pour mettre à jour la vidéo active
    updateActiveVideo() {
      console.log('🎬 Mise à jour vidéo active - currentIndex:', this.currentIndex);
      
      if (this.isCurrentSlideVideo) {
        this.activeVideoIndex = this.currentIndex;
        console.log('✅ Vidéo active définie:', this.activeVideoIndex);
        
        // Notifier toutes les vidéos de leur statut
        this.notifyVideoActiveStatus();
      } else {
        this.activeVideoIndex = null;
        console.log('❌ Pas de vidéo active (slide image)');
      }
    },

    // ✅ NOUVEAU : Notifier toutes les vidéos de leur statut actif/inactif
    notifyVideoActiveStatus() {
      Object.keys(this.$refs).forEach(refKey => {
        if (refKey.startsWith('video-')) {
          const videoIndex = parseInt(refKey.split('-')[1]);
          const videoComponent = this.$refs[refKey];
          const isActive = videoIndex === this.activeVideoIndex;
          
          if (Array.isArray(videoComponent)) {
            videoComponent.forEach(comp => {
              if (comp && typeof comp.setActiveStatus === 'function') {
                comp.setActiveStatus(isActive);
              }
            });
          } else if (videoComponent && typeof videoComponent.setActiveStatus === 'function') {
            videoComponent.setActiveStatus(isActive);
          }
          
          console.log(`📺 Vidéo ${videoIndex}:`, isActive ? 'ACTIVE' : 'inactive');
        }
      });
    },

    // ✅ AMÉLIORATION : Méthode stopAllVideos avec refs
    stopAllVideos() {
      // Utiliser les refs pour accéder aux composants vidéo
      Object.keys(this.$refs).forEach(refKey => {
        if (refKey.startsWith('video-')) {
          const videoComponent = this.$refs[refKey];
          if (Array.isArray(videoComponent)) {
            videoComponent.forEach(comp => {
              if (comp && typeof comp.stopVideoPlayback === 'function') {
                comp.stopVideoPlayback();
                comp.lecteurVideo = false;
              }
            });
          } else if (videoComponent && typeof videoComponent.stopVideoPlayback === 'function') {
            videoComponent.stopVideoPlayback();
            videoComponent.lecteurVideo = false;
          }
        }
      });
      
      // ✅ NOUVEAU : Reset la vidéo active
      this.activeVideoIndex = null;
      console.log('⏹️ Toutes les vidéos arrêtées');
    },
    
    isImage(media) {
      return media.includes('.avif');
    },
    isVideo(media) {
      return media.includes('.mp4');
    },

    // ✅ MÉTHODES DE NAVIGATION AMÉLIORÉES :
    goToNextSlide() {
      if (this.currentIndex < this.listeDiapos.length - 1) {
        this.stopAllVideos();
        this.currentIndex++;
        this.updateActiveVideo();
        console.log('📱 Slide suivant:', this.currentIndex);
      }
    },
    
    goToPreviousSlide() {
      if (this.currentIndex > 0) {
        this.stopAllVideos();
        this.currentIndex--;
        this.updateActiveVideo();
        console.log('📱 Slide précédent:', this.currentIndex);
      }
    },

    handleKeyboardInput(event) {
      if (event.key === 'ArrowLeft') {
        this.goToPreviousSlide();
      } else if (event.key === 'ArrowRight') {
        this.goToNextSlide();
      }
      // ✅ NOUVEAU : La touche Space est gérée par les composants vidéo individuels
    },

    // ✅ MÉTHODES TACTILES CORRIGÉES :
    onTouchStart(e) {
      // e.preventDefault();  // ← À retirer !
      if (e.touches && e.touches.length === 1 && !this.isSwipeInProgress) {
        this.touchStartX = e.touches[0].clientX;
        this.touchCurrentX = e.touches[0].clientX;
        this.touchStartTime = Date.now();
        this.isSwipeInProgress = true;
      }
    },

    onTouchMove(e) {
      if (this.isSwipeInProgress && Math.abs(e.touches[0].clientX - this.touchStartX) > 10) {
        e.preventDefault(); // ← Empêche le scroll seulement si swipe horizontal
        this.touchCurrentX = e.touches[0].clientX;
      }
    },

    onTouchEnd() {
      if (!this.isSwipeInProgress || this.touchStartX === null || this.touchCurrentX === null) {
        this.resetTouchState();
        return;
      }
      const deltaX = this.touchCurrentX - this.touchStartX;
      const deltaTime = Date.now() - this.touchStartTime;
      const absDeltaX = Math.abs(deltaX);

      if (absDeltaX > this.swipeThreshold && deltaTime < this.maxSwipeTime) {
        if (deltaX < 0 && this.currentIndex < this.listeDiapos.length - 1) {
          this.goToNextSlide();
        } else if (deltaX > 0 && this.currentIndex > 0) {
          this.goToPreviousSlide();
        }
      }
      setTimeout(() => {
        this.resetTouchState();
      }, 100);
    },

    onTouchCancel() {
      this.resetTouchState();
    },

    // ✅ NOUVELLE MÉTHODE : Réinitialiser l'état tactile
    resetTouchState() {
      this.touchStartX = null;
      this.touchCurrentX = null;
      this.touchStartTime = null;
      this.isSwipeInProgress = false;
      console.log('🔄 Touch state reset');
    },

    verifyImagesLoaded() {
      const images = this.listeDiapos.filter(this.isImage);
      let loadedCount = 0;
      if (images.length === 0) {
        this.$emit('images-loaded');
        return;
      }
      images.forEach((media) => {
        const img = new Image();
        img.src = `diapo/${this.resolution === 'mobile' ? '500px' : '1000px'}/${media}`;
        if (img.complete) {
          loadedCount++;
          if (loadedCount === images.length) this.$emit('images-loaded');
        } else {
          img.addEventListener('load', () => {
            loadedCount++;
            if (loadedCount === images.length) this.$emit('images-loaded');
          });
          img.addEventListener('error', () => {
            loadedCount++;
            if (loadedCount === images.length) this.$emit('images-loaded');
          });
        }
      });
    },
    updateSectionFromMediaName(inputString) {
      const segments = inputString.split('-');
      if (segments.length > 2) {
        const sectionSelected = parseInt(segments[2], 10) - 1;
        this.updateStoreUrl({ tag: 'section', value: sectionSelected });
      }
    },
    initializeDiaporama() {
      this.currentIndex = 0;
      this.imagesLoaded = {};
      this.verifyImagesLoaded();
      this.updateSectionFromMediaName(this.listeDiapos[0] || '');

      this.$nextTick(() => {
        this.listeDiapos.forEach((media, index) => {
          if (this.isImage(media)) {
            const img = new window.Image();
            img.src = `diapo/${this.resolution === 'mobile' ? '500px' : '1000px'}/${media}`;
            if (img.complete) {
              this.onImageLoaded(index);
            }
          }
        });
        setTimeout(() => {
          this.isComponentReady = true;
          this.updateActiveVideo();
        }, 100);
      });
    },

    // ✅ NOUVEAU : Force l'activation de la première vidéo
    forceActivateFirstVideo() {
      console.log('🔧 Force activation première vidéo');
      
      if (this.currentIndex === 0 && this.isCurrentSlideVideo) {
        const firstVideoRef = this.$refs['video-0'];
        
        if (firstVideoRef) {
          const component = Array.isArray(firstVideoRef) ? firstVideoRef[0] : firstVideoRef;
          if (component && typeof component.setActiveStatus === 'function') {
            component.setActiveStatus(true);
            console.log('✅ Première vidéo forcée comme active');
          }
        }
      }
    },
    onImageLoaded(index) {
      this.imagesLoaded[index] = true;
    },
  },
  
  watch: {
    langue() {
      this.currentIndex = 0;
      this.imagesLoaded = {};
      this.isComponentReady = false;
      this.stopAllVideos && this.stopAllVideos();
      this.initializeDiaporama();
    },
    sectionActive() {
      const chaineTofind = this.racineName + (this.sectionActive + 1);
      const idx = this.listeDiapos.findIndex((media) => media.includes(chaineTofind));
      if (idx >= 0) {
        this.stopAllVideos(); // ✅ Arrêter les vidéos avant de changer
        this.currentIndex = idx;
        this.updateActiveVideo(); // ✅ NOUVEAU
      }
    },
    
    currentIndex(newIndex, oldIndex) {
      if (newIndex !== oldIndex) {
        this.stopAllVideos(); // ✅ Arrêter toutes les vidéos lors du changement
        this.updateSectionFromMediaName(this.currentMedia);
        this.updateActiveVideo(); // ✅ NOUVEAU
      }
    },
    
    listeDiapos: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.imagesLoaded = {};
          this.isComponentReady = false;
          this.stopAllVideos(); // ✅ Arrêter les vidéos lors du changement de liste
          this.initializeDiaporama();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  
  mounted() {
    console.log('🚀 LecteurDiapo monté');
    window.addEventListener('keydown', this.handleKeyboardInput);
    
    this.$nextTick(() => {
      if (this.listeDiapos && this.listeDiapos.length > 0) {
        setTimeout(() => {
          this.isComponentReady = true;
          this.updateActiveVideo(); // doit notifier la première vidéo
        }, 200);
      }
    });
  },
  
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyboardInput);
  },
};
</script>

<style scoped>
#diaporama {
  display: flex;
  flex-direction: column;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  width: 100%;
  position: relative;
  /* ✅ NOUVEAU : Améliorer la gestion tactile */
  touch-action: pan-x; /* Permettre seulement les swipes horizontaux */
  -webkit-overflow-scrolling: touch; /* Améliorer le scroll sur iOS */
}

#diaporama, #slides, .slide {
  aspect-ratio: 1 / 1;
  width: var(--hauteur);
  /* ✅ CORRIGER : Utiliser min() pour prendre la plus petite valeur */
}

#slides {
  display: flex;
  margin: 0;
  transition: transform 0.7s ease-in-out;
  z-index: 1;
  /* ✅ NOUVEAU : Améliorer la gestion tactile */
  touch-action: pan-x; /* Permettre seulement les swipes horizontaux */
  -webkit-touch-callout: none; /* Désactiver le menu contextuel iOS */
  -webkit-user-select: none; /* Désactiver la sélection de texte */
  user-select: none;
}

#slides div {
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.slide {
  display: flex;
  justify-content: center;
  align-items: center;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
  position: relative;
  /* ✅ NOUVEAU : Empêcher la sélection et le drag */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
}

#navigation {
  position: absolute !important;
  bottom: 10px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  z-index: 999999 !important;
  pointer-events: auto !important; /* ✅ CORRECTION : Permettre les clics */
  background-color: var(--colorDark);
  height: 30px !important;
  border-radius: 20px;
}

button {
  background-image: url('/public/interface/small_back.svg');
  background-color: var(--colorDark) !important;
  color: var(--colorWhite) !important;
  border: none !important;
  cursor: pointer !important;
  border-radius: 50% !important;
  transition: all 0.5s !important;
  width: 27px !important;
  height: 27px !important;
}

.dot {
  width: 10px !important;
  height: 10px !important;
  border-radius: 100% !important;
  border: 2px solid var(--colorLight) !important;
  z-index: 999999 !important;
  transition: all 0.3s ease !important;
  margin: 0 4px;
}

.dot.active {
  background-color: var(--colorLight) !important;
  transform: scale(1.2) !important;
}

.video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: black;
  position: relative;
}

.video-loading {
  color: var(--colorLight);
  font-size: 14px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* ✅ NOUVEAU : Améliorer les performances sur mobile */
@media screen and (max-width: 1100px) {
  #diaporama, #slides, .slide {
    max-width: min(var(--largeurMaxMobile), 100vw) !important;
    max-height: min(var(--largeurMaxMobile), 100vw) !important;
    /* ✅ Optimisations pour les appareils tactiles */
    -webkit-transform: translate3d(0, 0, 0); /* Forcer l'accélération matérielle */
    transform: translate3d(0, 0, 0);
  }
  
  #slides {
    /* ✅ Améliorer les performances d'animation sur mobile */
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
    will-change: transform; /* Optimiser les changements de transform */
  }
}

.slide img.img-loaded {
  opacity: 1 !important;
  transition: opacity 1s;
  pointer-events: auto;
}
</style>